import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost } from '@app/src/api/fetchHooks'
import CreateNewRequestModal from '@app/src/components/CreateNewRequestModal'
import EmptyState from '@app/src/components/EmptyState'
import HasWriteAccess from '@app/src/components/HasWriteAccess'
import { useAmplitude } from '@app/src/context/AmplitudeContext'
import { QuestionnaireType } from '@app/src/context/CreationModalProgressContext'
import CreationModalProgressContextProvider from '@app/src/context/CreationModalProgressContextProvider'
import useCurrentProviderType from '@app/src/hooks/currentProviderType'
import { useDialogState } from '@app/src/hooks/mui-hooks'
import { useGetApiQueryFilters } from '@app/src/hooks/queryFilters'
import useAssessmentQuestionnaireInquiriesCount from '@app/src/hooks/useAssessmentQuestionnaireInquiriesCount'
import type Assessment from '@app/src/types/assessment'
import { SortOrder } from '@app/src/types/filter'
import type { Provider } from '@app/src/types/organizations'
import { AssessmentType } from '@app/src/types/resourceExplorer'
import { AmplitudeTrackingEvents, ONE_MINUTE_MS } from '@app/src/wf-constants'
import { ListAltOutlined } from '@mui/icons-material'
import { Box, Button, CircularProgress, Stack } from '@mui/material'
import { useFlags } from 'launchdarkly-react-client-sdk'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import ResourceCollectionScene from '../../ResourceCollection'
import AssessmentCompanyPageFilters from '../../ResourceCollection/Filters/AssessmentCompanyPageFilters'
import { Operators } from '../../ResourceCollection/Filters/useFilters'
import { useFetchAssessmentSettings } from '../useFetchAssessmentSettings'
import AssessmentCompanyPage from './AssessmentCompanyPage'

interface AssessmentsCompanyPageSceneProps {
  provider: Provider
}

const allowedFilters = ['assessmentTemplate.id', 'periodName', 'assessmentTemplateId']

const ASSESSMENT_INCLUDES = [
  'assessmentTemplate.rules',
  'assessmentTemplate.sections.rules',
  'provider.organization.country',
  'provider.organization.image',
]

const AssessmentsCompanyPageScene: React.FC<AssessmentsCompanyPageSceneProps> = ({ provider }) => {
  const { trackEvent } = useAmplitude()
  const { formatMessage } = useIntl()
  const { assessments: standardAssessmentsEnabled } = useFlags()
  const providerType = useCurrentProviderType()
  const [maxRowsForLongAnswers, setMaxRows] = useState(3)
  const [isCreateRequestDialogOpen, openCreateRequestDialog, closeCreateRequestDialog] = useDialogState()
  const userFilters = useGetApiQueryFilters(allowedFilters)
  const contentRef = useRef<HTMLDivElement>(null)
  const { assessmentSettings } = useFetchAssessmentSettings()

  const assessmentTemplateId = Number(
    userFilters.find(group => group.name === 'assessmentTemplate.id')?.filters[0]?.value,
  )
  const periodName = userFilters.find(group => group.name === 'periodName')?.filters[0]?.value?.toString()

  const enabledAssessmentTemplates = useMemo(
    () =>
      assessmentSettings
        .filter(setting => setting.isActivated)
        .map(setting => setting.assessmentTemplate)
        .filter(
          template => standardAssessmentsEnabled || template.assessmentType !== AssessmentType.StandardAssessment,
        ),
    [assessmentSettings, standardAssessmentsEnabled],
  )

  const selectedAssessmentTemplate = enabledAssessmentTemplates.find(template => template.id === assessmentTemplateId)

  const {
    items: [assessment],
    isLoading: isLoadingAssessment,
  } = useFetchCollectionWithPost<Assessment>({
    key: [FetchKey.AssessmentCollection, periodName],
    endpoint: endpoints.assessmentCollection,
    payload: {
      filter: [...userFilters, { name: 'provider.id', filters: [{ value: provider.id, operator: Operators.EqualTo }] }],
      include: ASSESSMENT_INCLUDES,
      sort: { target: 'createdAt', order: SortOrder.DESCENDING },
    },
    options: { staleTime: ONE_MINUTE_MS },
  })

  useEffect(() => {
    trackEvent({ name: AmplitudeTrackingEvents.Analyze.Assessment.ViewedPage, eventProps: { page: 'provider' } })
  }, [])

  const { inquiriesCount, isLoadingInquiriesCount, questionnaireTemplates } = useAssessmentQuestionnaireInquiriesCount(
    selectedAssessmentTemplate,
    Boolean(periodName && selectedAssessmentTemplate),
    provider.id,
    periodName,
  )

  const showEmptyState = !isLoadingInquiriesCount && !isLoadingAssessment && !assessment

  return (
    <ResourceCollectionScene
      enableScroll
      title=""
      filter={
        <AssessmentCompanyPageFilters
          provider={provider}
          contentRef={contentRef}
          allowedFilters={allowedFilters}
          enabledAssessmentTemplates={enabledAssessmentTemplates}
          assessmentTemplateId={assessmentTemplateId}
          setMaxRows={setMaxRows}
          showPdfExportButton={Boolean(assessment)}
        />
      }
      embedded
    >
      <Box ref={contentRef} height="100%">
        {isLoadingAssessment && (
          <Stack justifyContent="center" alignItems="center" height="100%" py={8}>
            <CircularProgress />
          </Stack>
        )}
        {showEmptyState && (
          <Stack flexGrow={1} justifyContent="center" alignItems="center" height="100%" pb={8} pr={4}>
            {inquiriesCount ? (
              <EmptyState
                iconComponent={ListAltOutlined}
                title={formatMessage({ id: 'baselineAssessment.providerPage.noResponses' })}
                description={formatMessage(
                  { id: 'baselineAssessment.providerPage.noResponseDescription' },
                  { providerType },
                )}
              />
            ) : (
              <EmptyState
                iconComponent={ListAltOutlined}
                title={formatMessage({ id: 'baselineAssessment.providerPage.noData' })}
                description={formatMessage({ id: 'baselineAssessment.providerPage.noDataDescription' })}
              >
                <HasWriteAccess>
                  <Button variant="outlined" onClick={openCreateRequestDialog}>
                    {formatMessage({ id: 'assessments.overviewGraph.noInquiriesEmptyState.action' })}
                  </Button>
                </HasWriteAccess>
              </EmptyState>
            )}
          </Stack>
        )}
        {assessment && (
          <Box pt={4} pr={3} height="100%">
            <AssessmentCompanyPage
              assessment={assessment}
              provider={provider}
              maxRowsForLongAnswers={maxRowsForLongAnswers}
              embedded
            />
          </Box>
        )}
      </Box>
      <CreationModalProgressContextProvider>
        <CreateNewRequestModal
          open={isCreateRequestDialogOpen}
          onClose={closeCreateRequestDialog}
          questionnaireType={QuestionnaireType.WF}
          disableInitialBack
          selectedQuestionnaireTemplates={questionnaireTemplates.filter(template => template.isStandardReport)}
        />
      </CreationModalProgressContextProvider>
    </ResourceCollectionScene>
  )
}

export default AssessmentsCompanyPageScene
